import { useEffect, useMemo, useState } from "react";

import { todayLocal } from "./event-dates";

/**
 * The current instant, re-read when the app comes back to the front. Held to
 * the minute so a focus that changes nothing does not render. It exists for
 * "has this event ended in its own zone", which `useToday`'s reader-zone day
 * cannot answer.
 */
export function useNow(): Date {
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60_000));

  useEffect(() => {
    const refresh = () => setMinute(Math.floor(Date.now() / 60_000));
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    refresh();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return useMemo(() => new Date(minute * 60_000), [minute]);
}

/**
 * The reader's calendar day, `yyyy-mm-dd`, as the event cards count from it.
 *
 * Read on mount and again when the page comes back: an installed app resumed
 * days later is not remounted, and would otherwise keep saying "Starts
 * tomorrow" for an event that began. Setting the same string again does not
 * render, so a focus that changes nothing costs nothing.
 */
export function useToday(): string {
  const [today, setToday] = useState(() => todayLocal(new Date()));

  useEffect(() => {
    const refresh = () => setToday(todayLocal(new Date()));
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    // Catches a day that turned between the first render and this effect.
    refresh();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return today;
}
